// Ported from Stephanet's MissedDaysAudit.jsx (Missed Days Management).
import React, { useEffect, useMemo, useState } from 'react'
import {
  ArrowRight, Ban, Banknote, CalendarCheck, CircleCheckBig, CircleX, Download, Filter, Fingerprint, MessageSquareWarning, Printer,
  RotateCcw, ScrollText, Search, Siren, Sparkles, Undo2,
} from 'lucide-react'
import * as hr from '../api'
import Loader from '../components/Loader'
import { download, fmtMoney, fmtNumber, toCSV } from '../format'
import { DateInput, Select } from '../components/Controls'
import { Chip, Kpi, STATUS_TONE } from './missed/shared'

// Missed Days Audit Trail: every action on missed day records — generation, approvals, leave, declines, explanations, payroll posting,
// biometric errors, escalations, reversals and cancellations — with the status before and after, filtered by action, employee and date

const EMPTY = { action: '', employee_id: '', start: '', end: '', q: '', order: 'desc' }
const ACTION = {
  Generated: ['blue', Sparkles],
  Approved: ['green', CircleCheckBig],
  'Leave Assigned': ['teal', CalendarCheck],
  Declined: ['red', CircleX],
  'Sent for Explanation': ['purple', MessageSquareWarning],
  'Posted to Payroll': ['indigo', Banknote],
  'Biometric Error': ['orange', Fingerprint],
  Escalated: ['darkred', Siren],
  Reversed: ['gray', Undo2],
  Cancelled: ['gray', Ban],
}
const STATUS = { ...STATUS_TONE, 'Posted to Payroll': 'indigo' }
const stamp = v => (v ? v.replace('T', ' ').slice(0, 19) : '—')
const byText = b => (b.role ? `${b.name} (${b.role})` : b.name)
const bal = v => (v === null || v === undefined ? '—' : fmtNumber(v))

export default function MissedDaysAudit() {
  const [f, setF] = useState(EMPTY)
  const [q, setQ] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => { const t = setTimeout(() => setF(x => (x.q === q ? x : { ...x, q })), 350); return () => clearTimeout(t) }, [q])
  useEffect(() => {
    setLoading(true)
    const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v))
    hr.mdAudit(Object.fromEntries(p)).then(d => { setData(d); setError(''); setTick(t => t + 1) }).catch(e => setError(e.message)).finally(() => setLoading(false))
  }, [f])

  const filtered = useMemo(() => Object.entries(f).some(([k, v]) => v && k !== 'order'), [f])
  if (error && !data) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!data) return <Loader title="Loading audit trail…" subtitle="Fetching the trail" />

  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  const clear = () => { setF(EMPTY); setQ('') }
  const k = data.kpis
  const emp = data.employees.find(x => String(x.id) === String(f.employee_id))
  const scope = [f.action || 'All actions', emp ? `${emp.employee_no} — ${emp.name}` : 'All employees',
    f.start || f.end ? `${f.start || 'start'} to ${f.end || 'date'}` : 'All dates'].join(' · ')
  const exportCsv = () => download(`missed-days-audit-trail${f.start ? `-${f.start}` : ''}${f.end ? `-to-${f.end}` : ''}.csv`, toCSV([
    { label: 'Date & Time', get: e => stamp(e.at) }, { label: 'Action', get: e => e.action }, { label: 'MD ID', get: e => e.reference },
    { label: 'Employee ID', get: e => e.employee_no }, { label: 'Employee Name', get: e => e.name }, { label: 'Date Missed', get: e => e.missed_date },
    { label: 'By', get: e => byText(e.by) }, { label: 'From Status', get: e => e.from_status }, { label: 'To Status', get: e => e.to_status },
    { label: 'Leave Used', get: e => (e.leave_type ? `${e.leave_type} Leave` : '') },
    { label: 'Balance Before', get: e => e.balance_before ?? '' }, { label: 'Balance After', get: e => e.balance_after ?? '' },
    { label: 'Deduction', get: e => e.deduction || '' }, { label: 'Comment', get: e => e.note },
  ], data.events))

  return (
    <div className="mdr mds mdau">
      <div className="mdr-top">
        <div>
          <h2>Missed Days Audit Trail</h2>
          <p>Complete log of all actions, approvals, declines, and changes on missed day records</p>
        </div>
        <div className="mdr-top-actions no-print">
          {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!data.events.length}><Download size={14} /> Export</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
      </div>
      <p className="print-only mds-print-period">{scope}</p>

      <div className="md-kpis mds-kpis mdau-kpis" key={`k${tick}`}>
        <Kpi label="Total Events" value={k.total} Ico={ScrollText} tone="blue" note={filtered ? 'Matching the filters' : 'All recorded actions'} />
        <Kpi label="Leave Assigned" value={k.leave} Ico={CalendarCheck} tone="teal" note="Days settled from leave" i={1} />
        <Kpi label="Declined" value={k.declined} Ico={CircleX} tone="red" note="Salary deduction required" i={2} />
        <Kpi label="Posted to Payroll" value={k.posted} Ico={Banknote} tone="purple" note="Deductions posted" i={3} />
      </div>

      <div className="mdr-card mdau-filters no-print">
        <div className="mds-card-title"><Filter size={15} /> Filter Events</div>
        <div className="mdau-filter-row">
          <label>Action
            <Select value={f.action} onChange={e => set('action', e.target.value)}>
              <option value="">All Actions</option>
              {data.actions.map(a => <option key={a} value={a}>{a}</option>)}
            </Select>
          </label>
          <label>Employee
            <Select value={f.employee_id} onChange={e => set('employee_id', e.target.value)}>
              <option value="">All Employees</option>
              {data.employees.map(x => <option key={x.id} value={x.id}>{x.employee_no} — {x.name}{x.status && !['active', 'probation'].includes(x.status) ? ` (${x.status})` : ''}</option>)}
            </Select>
          </label>
          <label>From Date<DateInput value={f.start} max={f.end || undefined} onChange={e => set('start', e.target.value)} /></label>
          <label>To Date<DateInput value={f.end} min={f.start || undefined} onChange={e => set('end', e.target.value)} /></label>
          <label>Order
            <Select value={f.order} onChange={e => set('order', e.target.value)}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </Select>
          </label>
          <label className="mdau-q">Search
            <span className="mdr-search"><Search size={14} /><input placeholder="MD ID, comment or user..." value={q} onChange={e => setQ(e.target.value)} /></span>
          </label>
          <button type="button" className="mdr-btn mdau-clear" onClick={clear} disabled={!filtered && f.order === 'desc'}><RotateCcw size={13} /> Clear</button>
        </div>
        {error && <span className="mds-error">{error}</span>}
      </div>

      <div className={`mdr-card mdau-card ${loading ? 'is-loading' : ''}`}>
        <div className="mds-table-head">
          <b>Event Log <span className="mdl-year">{data.total > data.shown ? `latest ${fmtNumber(data.shown)} of ${fmtNumber(data.total)}` : `${fmtNumber(data.total)} event(s)`}</span></b>
        </div>
        <ol className="mdau-list" key={`l${tick}`}>
          {data.events.map((e, i) => {
            const [tone, Ico] = ACTION[e.action] || ['gray', ScrollText]
            return (
              <li key={e.id} className={`mdau-ev t-${tone}`} style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                <span className="mdau-dot"><Ico size={15} /></span>
                <div className="mdau-body">
                  <div className="mdau-head">
                    <div className="mdau-title">
                      <Chip tone={tone}>{e.action}</Chip>
                      <span className="mdau-who">— {e.name || 'Unknown employee'}{e.employee_no ? <em> ({e.employee_no})</em> : null}</span>
                    </div>
                    <time>{stamp(e.at)}</time>
                  </div>
                  <div className="mdau-meta">
                    <span>MD: <b className="mdr-id">{e.reference}</b></span>
                    <span>Date Missed: <b>{e.missed_date || '—'}</b></span>
                    <span>By: <b>{byText(e.by)}</b></span>
                  </div>
                  {(e.from_status || e.to_status) && (
                    <div className="mdau-flow">
                      {e.from_status && <Chip tone={STATUS[e.from_status]}>{e.from_status}</Chip>}
                      {e.from_status && e.to_status && <ArrowRight size={14} className="mdau-arrow" />}
                      {e.to_status && <Chip tone={STATUS[e.to_status]}>{e.to_status}</Chip>}
                    </div>
                  )}
                  {e.leave_type && (
                    <div className="mdau-detail">
                      Leave Used: <b>{e.leave_type} Leave</b>
                      {(e.balance_before !== null || e.balance_after !== null) && <> <i>|</i> Balance: <b>{bal(e.balance_before)} → {bal(e.balance_after)}</b></>}
                    </div>
                  )}
                  {e.deduction > 0 && <div className="mdau-detail bad">Deduction: <b>{fmtMoney(e.deduction)}</b></div>}
                  {e.note && <blockquote className="mdau-note">“{e.note}”</blockquote>}
                </div>
              </li>
            )
          })}
          {!data.events.length && <li className="mdr-empty mdau-empty">{filtered ? 'No events match these filters.' : 'No actions have been recorded on missed day records yet.'}</li>}
        </ol>
      </div>
    </div>
  )
}
